import { AccService, WhoAmI } from 'api/schema'
import { createContext, useContext } from 'react'
import { LOGIN_ERROR_TYPES, PROGRESS_TYPES } from 'utils/constants'
import { Updater, useImmer } from 'use-immer'

interface IAccountState {
  loginProgress: PROGRESS_TYPES
  restorePasswordProgress: PROGRESS_TYPES
  loginProgressError: LOGIN_ERROR_TYPES
  userLoggedIn: boolean
  id: number
  email: string
  firstName?: string
  lastName?: string
  project_default_id?: number
}

interface IAccountActions {
  signin: (login: string, password: string) => Promise<any>
  signout: () => Promise<any>
  fetchUser: () => Promise<any>
  restorePassword: (email: string) => Promise<any>
}

export const initialAccountState: IAccountState = {
  loginProgress: PROGRESS_TYPES.IDLE,
  restorePasswordProgress: PROGRESS_TYPES.IDLE,
  loginProgressError: LOGIN_ERROR_TYPES.NONE,
  userLoggedIn: false,
  id: 0,
  email: '',
  firstName: undefined,
  lastName: undefined,
  project_default_id: undefined,
}

export const UserProviderContext = createContext<{
  state: IAccountState
  setState: Updater<IAccountState>
  actions: IAccountActions
} | null>(null)

UserProviderContext.displayName = 'User'

export const useUser = () => useContext(UserProviderContext)

export const UserProvider = ({ initialState, children }: { initialState: IAccountState; children: JSX.Element }) => {
  const [state, setState] = useImmer(initialState)
  const actions: IAccountActions = {
    signin: async (email: string, password: string) => {
      try {
        setState((draft: IAccountState) => {
          draft.userLoggedIn = false
          draft.loginProgress = PROGRESS_TYPES.WORK
          draft.loginProgressError = LOGIN_ERROR_TYPES.NONE
        })
        const data = await AccService.accLoginCreate({ email, password })
        if ((data as any).status === 'error') {
          setState({
            ...state,
            userLoggedIn: false,
            loginProgress: PROGRESS_TYPES.ERROR,
            loginProgressError: LOGIN_ERROR_TYPES.INVALID_EMAIL_OR_PASSWORD,
          })
        } else {
          const accountState = normalizeAccount(data)
          setState({ ...accountState, userLoggedIn: true, loginProgress: PROGRESS_TYPES.SUCCESS })
        }
      } catch (error) {
        console.error(error)
        setState({
          ...state,
          userLoggedIn: false,
          loginProgress: PROGRESS_TYPES.ERROR,
          loginProgressError: LOGIN_ERROR_TYPES.ERROR,
        })
      }
    },
    signout: async () => {
      try {
        await AccService.accLogoutCreate()
        setState({ ...initialAccountState, userLoggedIn: false })
      } catch (error) {
        setState({ ...initialAccountState, userLoggedIn: false })
      }
    },
    fetchUser: async () => {
      const accountState = await getInitialAccountState()
      setState({ ...accountState })
    },
    restorePassword: async (email: string) => {
      try {
        setState((draft: IAccountState) => {
          draft.restorePasswordProgress = PROGRESS_TYPES.WORK
        })
        await AccService.accPasswordResetCreate({ email })
        setState((draft: IAccountState) => {
          draft.restorePasswordProgress = PROGRESS_TYPES.SUCCESS
        })
      } catch (error) {
        setState((draft: IAccountState) => {
          draft.restorePasswordProgress = PROGRESS_TYPES.ERROR
        })
      }
    },
  }

  return (
    <UserProviderContext.Provider value={{ state, setState, actions }}>
      {children ? children : ''}
    </UserProviderContext.Provider>
  )
}

const normalizeAccount = (accountRaw: WhoAmI): IAccountState => {
  return {
    id: accountRaw.id,
    email: accountRaw.email,
    restorePasswordProgress: PROGRESS_TYPES.IDLE,
    loginProgress: PROGRESS_TYPES.SUCCESS,
    loginProgressError: LOGIN_ERROR_TYPES.NONE,
    userLoggedIn: true,
    firstName: accountRaw.first_name || '',
    lastName: accountRaw.last_name || '',
    project_default_id: accountRaw.project_default_id,
  }
}

export const getInitialAccountState = async (): Promise<IAccountState> => {
  try {
    const data = await AccService.accWhoamiRetrieve()
    return normalizeAccount(data)
  } catch (error) {
    return initialAccountState
  }
}
